import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  cameraViewMatrix,
  cross,
  dFdx,
  dFdy,
  dot,
  mix,
  modelPosition,
  mx_noise_float,
  mx_noise_vec3,
  normalize,
  normalView,
  positionView,
  positionWorld,
  sign,
  smoothstep,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import { mulberry32 } from '../math/rng';
import { perpendicularBasis } from '../universe/Tunnel';
import { groundAlongAxis, lipDepthAt, type TerrainMouth } from './TerrainMouth';

type RGB = [number, number, number];

export interface MouthRockStyle {
  light: RGB;
  dark: RGB;
  /** Optional glowing moss in the cracks (linear RGB radiance). */
  glow?: RGB;
}

/**
 * A ring of boulders around a cave mouth, piled higher over the tunnel and open
 * where the walkway meets the ground, so the hole reads as a cave entrance.
 * Returns a mesh whose instances are relative to the mouth point (anchor it at
 * hollowCenter + mouth.point).
 */
/** How far (m) the ring's rocks keep from the doorway's standing stones, beyond their own size. */
const STONE_CLEARANCE = 2.2;

export function buildMouthRocks(
  m: TerrainMouth,
  hollowRadius: number,
  style: MouthRockStyle,
  lightsNode: THREE.LightsNode,
  seed = 1,
): THREE.InstancedMesh {
  // Corners shared between faces (an icosahedron comes with three per face, each face lit
  // flat), so the normals are smooth: the facets were a low-poly game's.
  const plain = new THREE.IcosahedronGeometry(1, 2);
  plain.deleteAttribute('normal');
  plain.deleteAttribute('uv');
  const geo = mergeVertices(plain, 1e-4);
  const pos = geo.getAttribute('position');
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const k =
      1 +
      0.18 * Math.sin(x * 4.1 + y * 2.9) * Math.cos(z * 3.7 - x * 1.9) +
      0.07 * Math.sin(x * 11.3 - z * 9.1 + y * 7.7);
    // Flat-bottomed and a little squat, with a sharper break on one side.
    pos.setXYZ(i, x * k * 1.1, (y > 0 ? y * 0.95 : y * 0.45) * k, z * k * (z > 0.3 ? 0.85 : 1));
  }
  geo.computeVertexNormals();

  const mat = new THREE.MeshStandardNodeMaterial({ roughness: 0.92, metalness: 0 });
  mat.lightsNode = lightsNode;
  // Stable coordinates: world position relative to the anchored mesh.
  const p = positionWorld.sub(modelPosition);
  const n1 = mx_noise_float(p.mul(0.45));
  const n2 = mx_noise_float(p.mul(2.3));
  mat.colorNode = mix(vec3(...style.dark), vec3(...style.light), smoothstep(-0.4, 0.5, n1.add(n2.mul(0.3))));
  // Worn half round (the face's own normal, from the derivatives, half in), with grain.
  const face = normalize(cross(dFdx(positionView), dFdy(positionView)));
  const faced = face.mul(sign(dot(face, positionView.negate())));
  const grain = cameraViewMatrix.mul(vec4(mx_noise_vec3(p.mul(1 / 0.35)), 0)).xyz;
  mat.normalNode = normalize(mix(faced, normalView, 0.6).add(grain.mul(0.3)));
  if (style.glow) {
    mat.emissiveNode = vec3(...style.glow).mul(smoothstep(0.5, 0.62, n2.add(n1.mul(0.2))));
  }

  // Placement: around the entry cylinder where it meets the plateau.
  const rnd = mulberry32(seed);
  const plateauR = hollowRadius - m.height;
  // The ground in a direction from the hollow centre: the plateau, dipping on the lip.
  const groundAt = (dir: THREE.Vector3): THREE.Vector3 =>
    dir.clone().multiplyScalar(plateauR + lipDepthAt(m, plateauR, dir));
  const nA = new THREE.Vector3();
  const nB = new THREE.Vector3();
  perpendicularBasis(m.axis, nA, nB);
  const up = m.dir.clone().negate();
  // Walking direction down into the cave, along the ground.
  const horiz = m.axis.clone().addScaledVector(up, -m.axis.dot(up)).normalize();
  const side = new THREE.Vector3().crossVectors(up, horiz).normalize();
  const local = new THREE.Vector3();
  // The doorway's standing stones (see the lintel below), on the ground beside the far rim:
  // the ring's rocks keep clear of them, or they hid the stones and the lintel seemed to float.
  const farRim = m.radius / Math.max(Math.abs(m.axis.dot(up)), 0.2);
  const stoneAlong = farRim - 1.6;
  const stoneSide = m.radius + 2.6;
  const stones = [-1, 1].map((s) =>
    groundAt(
      local
        .copy(m.point)
        .addScaledVector(horiz, stoneAlong)
        .addScaledVector(side, s * stoneSide)
        .normalize(),
    ).sub(m.point),
  );
  const apart = new THREE.Vector3();
  const nearStone = (rel: THREE.Vector3, size: number): boolean =>
    stones.some((st) => {
      const d = apart.subVectors(rel, st);
      return d.addScaledVector(up, -d.dot(up)).length() < STONE_CLEARANCE + size;
    });
  const off = new THREE.Vector3();
  const o = new THREE.Vector3();
  const tangential = new THREE.Vector3();
  const matrices: THREE.Matrix4[] = [];
  const q = new THREE.Quaternion();
  const qUp = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const count = 34;
  for (let k = 0; k < count; k++) {
    const phi = ((k + rnd() * 0.6) / count) * Math.PI * 2;
    for (let layer = 0; layer < 2; layer++) {
      const pre = rnd();
      // Rough size first (the far side is refined below), so every rock stays clear of the hole.
      const guess = (1.0 + pre * 1.3) * (layer === 1 ? 1.3 : 1);
      const rho0 = m.radius + 0.4 + guess * 1.1 + (layer === 1 ? 1.5 + rnd() * 2 : rnd() * 0.6);
      off
        .copy(nA)
        .multiplyScalar(rho0 * Math.cos(phi))
        .addScaledVector(nB, rho0 * Math.sin(phi));
      tangential.copy(off).addScaledVector(up, -off.dot(up)).normalize();
      const along0 = tangential.dot(horiz);
      const grow = 1 + 1.4 * Math.max(0, along0);
      const rho = rho0 + guess * (grow - 1) * 1.1;
      off
        .copy(nA)
        .multiplyScalar(rho * Math.cos(phi))
        .addScaledVector(nB, rho * Math.sin(phi));
      // On the ground: the plateau, dipping on the lip.
      const t = groundAlongAxis(m, plateauR, o.copy(m.point).add(off));
      const rel = off.clone().addScaledVector(m.axis, t);
      // Leave the walkway open: no rocks where the tunnel floor meets the ground.
      tangential.copy(rel).addScaledVector(up, -rel.dot(up)).normalize();
      const along = tangential.dot(horiz);
      if (along < -0.45) continue;
      // Bigger rocks over the tunnel, a low ring at the sides.
      const size = guess * grow;
      qUp.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rnd() * Math.PI * 2);
      q.premultiply(qUp);
      scale.set(size * (0.8 + rnd() * 0.5), size * (0.7 + rnd() * 0.6), size * (0.8 + rnd() * 0.5));
      rel.addScaledVector(up, -0.3 * scale.y);
      // (After its random numbers are drawn, so the rest of the ring keeps its layout.)
      if (nearStone(rel, Math.max(scale.x, scale.z))) continue;
      matrices.push(new THREE.Matrix4().compose(rel, q, scale));
    }
  }
  // A mound of big rocks over the tunnel, beyond the far rim, so the hole reads as a
  // way in under the rock. Far enough out that they stay above the tunnel roof.
  for (let k = 0; k < 11; k++) {
    const size0 = 2.6 + rnd() * 2.8;
    // The far rim is ~r / sin(slope) out; keep each rock's whole footprint beyond it.
    const along = m.radius / 0.4 + 1 + size0 * 1.25 + rnd() * 9;
    const lateral = (rnd() * 2 - 1) * (4 + along * 0.25);
    local.copy(m.point).addScaledVector(horiz, along).addScaledVector(side, lateral).normalize();
    const rel = groundAt(local).sub(m.point);
    const size = size0 - Math.abs(lateral) * 0.12;
    qUp.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
    q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rnd() * Math.PI * 2);
    q.premultiply(qUp);
    scale.set(size * (0.9 + rnd() * 0.4), size * (0.8 + rnd() * 0.5), size * (0.9 + rnd() * 0.4));
    rel.addScaledVector(up, -0.12 * scale.y);
    matrices.push(new THREE.Matrix4().compose(rel, q, scale));
  }
  // A lintel over the far end of the hole, on two standing stones: from the approach
  // the stair runs down into the dark under a slab of rock, like a doorway. The stones stand
  // taller than the rim's rocks: hidden among them, the lintel seemed to float like a saucer.
  const place = (along: number, lateral: number, lift: number, sx: number, sy: number, sz: number) => {
    local.copy(m.point).addScaledVector(horiz, along).addScaledVector(side, lateral).normalize();
    const rel = groundAt(local).sub(m.point).addScaledVector(up, lift);
    matrices.push(
      new THREE.Matrix4()
        .makeBasis(side, up, horiz)
        .scale(new THREE.Vector3(sx, sy, sz))
        .setPosition(rel),
    );
  };
  for (const s of [-1, 1]) place(stoneAlong, s * stoneSide, 0.6, 2.0, 3.6, 2.1);
  place(farRim - 0.6, 0, 4.6, m.radius + 3.8, 1.6, 2.6);
  const mesh = new THREE.InstancedMesh(geo, mat, matrices.length);
  for (const [i, mm] of matrices.entries()) mesh.setMatrixAt(i, mm);
  mesh.instanceMatrix.needsUpdate = true;
  mesh.frustumCulled = false;
  mesh.name = 'mouth rocks';
  return mesh;
}

/** One merged collision trimesh for all the rocks (relative to the same anchor). */
export function mouthRocksCollision(mesh: THREE.InstancedMesh): {
  positions: Float32Array;
  indices: Uint32Array;
} {
  const geo = mesh.geometry;
  const pos = geo.getAttribute('position');
  const index = geo.getIndex();
  const indexCount = index ? index.count : pos.count;
  const n = mesh.count;
  const positions = new Float32Array(n * pos.count * 3);
  const indices = new Uint32Array(n * indexCount);
  const m = new THREE.Matrix4();
  const v = new THREE.Vector3();
  for (let i = 0; i < n; i++) {
    mesh.getMatrixAt(i, m);
    for (let j = 0; j < pos.count; j++) {
      v.fromBufferAttribute(pos, j).applyMatrix4(m);
      positions.set([v.x, v.y, v.z], (i * pos.count + j) * 3);
    }
    for (let j = 0; j < indexCount; j++)
      indices[i * indexCount + j] = (index ? index.getX(j) : j) + i * pos.count;
  }
  return { positions, indices };
}
